export type VoicePhase = 'connecting' | 'connected' | 'ended' | 'error'
export type VoiceView = { phase: VoicePhase; note: string; caption: string; audioBlocked: boolean }

async function request(path: string, body?: unknown, keepalive = false) {
  const response = await fetch(`/api/voice/live${path}`, {
    method: body === undefined ? 'GET' : 'POST', credentials: 'include', keepalive,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: AbortSignal.timeout(35_000),
  })
  const data = await response.json()
  if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Live voice request failed')
  return data
}

export async function liveVoiceAvailable(): Promise<boolean> {
  const data = await request('/availability')
  return data.available === true
}

/** One user-started call. No automatic retries or microphone reacquisition. */
export class LiveVoiceCall {
  readonly id = crypto.randomUUID()
  private stopped = false
  private created = false
  private pc: RTCPeerConnection | null = null
  private dc: RTCDataChannel | null = null
  private stream: MediaStream | null = null
  private audio: HTMLAudioElement | null = null
  private timers: ReturnType<typeof setTimeout>[] = []
  private view: VoiceView = { phase: 'connecting', note: 'Connecting…', caption: '', audioBlocked: false }
  private speaker = ''

  constructor(private chatId: string, private topicId: string | null, private onView: (view: VoiceView) => void) {}

  get active() { return !this.stopped }

  private update(values: Partial<VoiceView>) {
    this.view = { ...this.view, ...values }
    this.onView(this.view)
  }

  async play() {
    try {
      await this.audio?.play()
      this.update({ audioBlocked: false })
    } catch {
      this.update({ audioBlocked: true })
    }
  }

  async start() {
    this.update({ phase: 'connecting' })
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('Live voice needs a secure browser with microphone access')
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false })
      if (this.stopped) { stream.getTracks().forEach(track => track.stop()); return }
      this.stream = stream
      const pc = new RTCPeerConnection()
      this.pc = pc
      this.audio = new Audio()
      this.audio.autoplay = true
      pc.ontrack = event => {
        if (this.stopped || !this.audio) return
        this.audio.srcObject = event.streams[0] ?? new MediaStream([event.track])
        void this.play()
      }
      pc.onconnectionstatechange = () => {
        if (this.stopped) return
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          this.end('Connection lost. Saved text and agent work remain available.', true)
        } else if (pc.connectionState === 'disconnected') {
          this.update({ note: 'Connection interrupted…' })
          this.timers.push(setTimeout(() => {
            if (!this.stopped && pc.connectionState === 'disconnected') this.end('Connection lost. Start a new call to reconnect.', true)
          }, 8000))
        } else if (pc.connectionState === 'connected') {
          this.update({ note: 'Listening · you can interrupt' })
        }
      }
      stream.getTracks().forEach(track => pc.addTrack(track, stream))
      const dc = pc.createDataChannel('oai-events')
      this.dc = dc
      dc.onmessage = event => {
        if (this.stopped) return
        try {
          const data = JSON.parse(String(event.data))
          if (data.type === 'session.started') this.update({ phase: 'connected', note: 'Listening · you can interrupt' })
          if (data.type === 'session.delegation.created') this.update({ note: 'Topic agent is working · answer will be saved here' })
          if (data.type === 'session.input_transcript.delta' || data.type === 'session.output_transcript.delta') {
            const speaker = data.type === 'session.input_transcript.delta' ? 'You' : 'Voice'
            if (typeof data.delta === 'string') {
              const caption = this.speaker === speaker ? this.view.caption + data.delta : `${speaker}: ${data.delta}`
              this.speaker = speaker
              this.update({ caption: caption.slice(-500) })
            }
          }
          if (data.type === 'session.closed') this.end('Call ended. Transcript and agent replies are saved here.')
          if (data.type === 'error') this.end('Voice provider error. Saved agent work is unaffected.', true)
        } catch { this.end('Unreadable voice event. Start a new call to retry.', true) }
      }
      dc.onclose = () => { if (!this.stopped) this.end('Voice connection closed. Saved text remains here.', true) }
      await pc.setLocalDescription(await pc.createOffer())
      if (pc.iceGatheringState !== 'complete') {
        await new Promise<void>((resolve, reject) => {
          const done = () => { clearTimeout(timer); pc.removeEventListener('icegatheringstatechange', check) }
          const check = () => { if (pc.iceGatheringState === 'complete') { done(); resolve() } }
          const timer = setTimeout(() => { done(); reject(new Error('Microphone connection timed out')) }, 10_000)
          pc.addEventListener('icegatheringstatechange', check)
          check()
        })
      }
      if (this.stopped) return
      const data = await request('/sessions', { id: this.id, chat_id: this.chatId, topic_id: this.topicId, sdp: pc.localDescription?.sdp })
      this.created = true
      if (this.stopped) { this.closeRemote(); return }
      await pc.setRemoteDescription({ type: 'answer', sdp: data.sdp })
      this.timers.push(setTimeout(() => this.end('Ten-minute trial ended. Your transcript is saved.'), data.max_seconds * 1000))
      this.timers.push(setTimeout(() => {
        if (!this.stopped && this.view.phase === 'connecting') this.end('Voice did not become ready. Start again to retry.', true)
      }, 20_000))
      void this.poll()
    } catch (error) {
      if (!this.stopped) this.end(error instanceof Error ? error.message : 'Could not start live voice', true)
    }
  }

  private async poll() {
    if (this.stopped) return
    try {
      const data = await request(`/sessions/${this.id}`)
      if (this.stopped) return
      if (data.status === 'closed' || data.status === 'interrupted') {
        this.end(data.error || 'Call ended. Transcript and agent answers are saved.', data.status === 'interrupted')
        return
      }
    } catch {
      if (!this.stopped) this.end('Cannot reach AgentChat. Microphone stopped; saved agent work is unaffected.', true)
      return
    }
    this.timers.push(setTimeout(() => void this.poll(), 2000))
  }

  private closeRemote() {
    // Browser navigation can race the create response. Re-send after that
    // response arrives; the backend also enforces the ten-minute ceiling.
    void request(`/sessions/${this.id}/close`, {}, true).catch(() => {})
  }

  end(note = 'Microphone stopped. Queued or running agent work continues.', failed = false) {
    if (this.stopped) return
    this.stopped = true
    this.timers.forEach(clearTimeout)
    this.timers = []
    this.stream?.getTracks().forEach(track => track.stop())
    this.stream = null
    this.audio?.pause()
    if (this.audio) this.audio.srcObject = null
    this.audio = null
    if (this.dc?.readyState === 'open') {
      this.dc.send(JSON.stringify({ type: 'session.close', event_id: crypto.randomUUID() }))
    }
    if (this.created) this.closeRemote()
    // Give session.closed its documented grace period, without keeping mic
    // capture or playback alive. The server owns final transcript capture.
    const pc = this.pc
    setTimeout(() => pc?.close(), 15_000)
    this.update({ phase: failed ? 'error' : 'ended', note, audioBlocked: false })
  }
}
